"use client"

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Image from 'next/image';
import React from "react";
import { FaCircleInfo, FaSnowflake, FaCircleExclamation, FaSun } from 'react-icons/fa6';
import AbreiseIcon from './gfx/icons/abreise';
import AnreiseIcon from './gfx/icons/anreise';
import CaravanIcon from './gfx/icons/caravan';
import EntsorgungIcon from './gfx/icons/entsorgung';
import HundIcon from './gfx/icons/hund';
import KurtaxeIcon from './gfx/icons/kurtaxe';
import LalabarIcon from './gfx/icons/lalabar';
import ReservierungIcon from './gfx/icons/reservierung';
import StromIcon from './gfx/icons/strom';
import ToilettenIcon from './gfx/icons/toiletten';
import SurfschuleIcon from '@/components/gfx/icons/surfschule-logo.png';
import kurUndTourismus from "@/components/gfx/kur-und-tourismus-zingst.jpg";
import fresenbruch from "@/components/gfx/fresenbruch.jpg";
import wellnessCamp from "@/components/gfx/wellness-camp.jpg";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTicketSettings } from "@/hooks/use-ticket-settings";

export default function Information() {
    const { isWinterModeActive, isLoading } = useTicketSettings();
    const winter = !isLoading && isWinterModeActive;

    const { ref, inView } = useInView({
        threshold: 0.05,
        triggerOnce: true
    });

    return (
        <div ref={ref}>
            {winter && (
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="mx-auto mb-12 w-full max-w-4xl px-4"
                >
                    <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 shadow-xl">
                        <div className="flex items-start gap-4">
                            <div className="rounded-2xl bg-amber-400/20 p-3">
                                <FaSnowflake className="h-8 w-8 text-amber-400" />
                            </div>
                            <div>
                                <h4 className="mb-2 font-special text-2xl font-bold text-white">
                                    Winterinfo
                                </h4>
                                <p className="mb-4 text-sm font-medium text-amber-300">
                                    Oktober - Anfang April
                                </p>
                                <div className="mb-4 inline-block rounded-full bg-amber-400/20 px-4 py-2">
                                    <p className="font-semibold text-amber-400">
                                        Wir haben 365 Tage im Jahr geöffnet!
                                    </p>
                                </div>
                                <p className="text-slate-300">
                                    Im Winter bieten wir euch gesicherte, feste Stellplätze im vorderen Bereich auf Rasengittersteinen, da der hintere Bereich aufgrund möglicher Überschwemmungen geschlossen bleibt. Toiletten und Duschen auf dem Platz sind witterungsbedingt nicht verfügbar, aber keine Sorge: Beheizte Toiletten findet ihr nur 50 Meter entfernt am Strandübergang.
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 md:grid-cols-2 lg:grid-cols-3">
                <InfoCard
                    title="Surfen & Meer"
                    content="Direkt neben unserem Stellplatz befindet sich die Surfschule Zingst mit Kursen und Camps für Windsurfen, Wellenreiten, Kitesurfen, Wing-Surfen, Foildrive und Stand-Up Paddling."
                    icon={<Image src={SurfschuleIcon} alt="Surfschule Zingst" className="h-full w-auto" />}
                    delay={0}
                    inView={inView}
                    winterNote={winter ? "von Oktober - April ist die Surfschule geschlossen" : undefined}
                    highlight
                >
                    <a href="https://surfandwave.club" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700">
                        mehr zur Surfschule →
                    </a>
                </InfoCard>

                <InfoCard
                    title="Arbeiten am Stellplatz"
                    content="Lust auf einen Sommer am Meer? Wir suchen freundliche Unterstützung als Hausmeister/Parkplatzwächter - ob mit eigenem Wohnmobil oder in einem unserer Caravans. Genieße das Strandleben und werde Teil unseres Teams!"
                    icon={<FaSun className="h-full w-full" />}
                    delay={0.1}
                    inView={inView}
                >
                    <a href="mailto:info@stellplatz-zingst.de?subject=Bewerbung%20Hausmeister%2FParkplatzwächter" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700">
                        jetzt bewerben →
                    </a>
                </InfoCard>

                <InfoCard
                    title="Reservierung"
                    content="Keine Reservierung möglich. Einfach anreisen, Platz suchen und erst dann online bezahlen. Die Kurtaxe wird bei der Bezahlung automatisch mit abgerechnet."
                    icon={<ReservierungIcon className="h-full w-full fill-current" />}
                    delay={0.05}
                    inView={inView}
                />

                <InfoCard
                    title="Anreise 24/7"
                    content="Die Anreise ist jederzeit möglich. Erst einen freien Platz suchen, dann online bezahlen."
                    icon={<AnreiseIcon className="h-full w-full fill-current" />}
                    delay={0.1}
                    inView={inView}
                >
                    <a href="#anfahrt" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700">
                        Anfahrtsbeschreibung →
                    </a>
                </InfoCard>

                <InfoCard
                    title="Abreise bis 9 Uhr"
                    content="Die Abreise sollte bis 9 Uhr (morgens) stattfinden. Gerne kann ein Tages-Ticket gelöst werden um später abzureisen."
                    icon={<AbreiseIcon className="h-full w-full fill-current" />}
                    delay={0.2}
                    inView={inView}
                />

                <InfoCard
                    title="Wohnanhänger"
                    content="Wohnwagen und Caravans sind herzlich willkommen. PKWs bitte auf dem Parkplatz parken. Die Gebühr beträgt 35,- € pro Gespann und Nacht."
                    icon={<CaravanIcon className="h-full w-full fill-current" />}
                    delay={0.3}
                    inView={inView}
                />

                <InfoCard
                    title="Entsorgung"
                    content="Bei uns gibt es keine Entsorgung, aber in naher Umgebung gibt es öffentliche Entsorgungs-Stationen:"
                    icon={<EntsorgungIcon className="h-full w-full fill-current" />}
                    delay={0.4}
                    inView={inView}
                >
                    <ul className="mt-3 space-y-2 text-sm">
                        <li>
                            <Popover>
                                <PopoverTrigger className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700">
                                    • Campingplatz Fresenbruch <FaCircleInfo className="h-3 w-3" />
                                </PopoverTrigger>
                                <PopoverContent className="w-80">
                                    <p className="mb-3 text-sm text-slate-600">Von unserem Stellplatz nach links ausfahren und der Seestraße bis zum Campingplatz Fresenbruch folgen.</p>
                                    <Image src={fresenbruch} alt="Campingplatz Fresenbruch" className="rounded-lg" quality={80} />
                                </PopoverContent>
                            </Popover>
                        </li>
                        <li>
                            <Popover>
                                <PopoverTrigger className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700">
                                    • Campingplatz Düne 6 <FaCircleInfo className="h-3 w-3" />
                                </PopoverTrigger>
                                <PopoverContent className="w-80">
                                    <p className="mb-3 text-sm text-slate-600">Nach links ausfahren, der Hauptstraße bis zum Kreisverkehr folgen, dann links zum Wellness-Camp Düne 6.</p>
                                    <Image src={wellnessCamp} alt="Campingplatz Düne 6" className="rounded-lg" quality={80} />
                                </PopoverContent>
                            </Popover>
                        </li>
                    </ul>
                </InfoCard>

                <InfoCard
                    title="Hunde"
                    content="Hunde sind bei uns herzlich willkommen. Direkt neben unserem Stellplatz befindet sich die neueste Attraktion für Hundeliebhaber - der Hundespielplatz!"
                    icon={<HundIcon className="h-full w-full fill-current" />}
                    delay={0.5}
                    inView={inView}
                />

                <InfoCard
                    title="Sanitäranlagen"
                    content="Wir haben seit 2020 vier neue Duschen und vier Toiletten. Der Eintritt für die Dusche beträgt 50 Cent, sowie 1,- € für Warmwasser. Toiletten sind genderfrei und kostenlos."
                    icon={<ToilettenIcon className="h-full w-full fill-current" />}
                    delay={0.6}
                    inView={inView}
                    winterNote={winter ? "von Oktober - April sind die Sanitäranlagen geschlossen" : undefined}
                />

                <InfoCard
                    title="Stromversorgung"
                    content="Vor Ort sind 9 Stromsäulen mit je 4 Steckplätzen verfügbar. Die Automaten können mit Münzgeld bedient werden: 1 kWh = 1,- €. Es wird der übliche CEE 3-Pin Camping-Stecker benötigt."
                    icon={<StromIcon className="h-full w-full fill-current" />}
                    delay={0.7}
                    inView={inView}
                    winterNote={winter ? "von Oktober - April ist kein Strom verfügbar" : undefined}
                />

                <InfoCard
                    title="Brötchen & Mehr"
                    content="Unser Kaffee ist im ganzen Ort bekannt. Morgens backen wir frische Brötchen, es gibt Kuchen, Fischbrötchen und weitere leckere Snacks direkt vor Ort."
                    icon={<LalabarIcon className="h-full w-full fill-current" />}
                    delay={0.8}
                    inView={inView}
                    winterNote={winter ? "von Oktober - April ist die LaLa Bar geschlossen" : undefined}
                >
                    <a href="https://lalasurfbar.de" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700">
                        mehr zur Lala Bar →
                    </a>
                </InfoCard>

                <InfoCard
                    title="Kurtaxe"
                    content=""
                    icon={<KurtaxeIcon className="h-full w-full fill-current" />}
                    delay={1.0}
                    inView={inView}
                >
                    <p className="text-sm text-slate-600">
                        In Zingst ist die Kurtaxe für alle Übernachtungsgäste verpflichtend. Darum musst du dich aber nicht kümmern: Die Kurtaxe wird bei der Online-Bezahlung automatisch mit abgerechnet (zusätzlich zum Ticketpreis). Eure{' '}
                        <Popover>
                            <PopoverTrigger className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700">
                                Gästekarte <FaCircleInfo className="h-3 w-3" />
                            </PopoverTrigger>
                            <PopoverContent className="w-80">
                                <p className="mb-3 text-sm text-slate-600">Die Gästekarte bietet zahlreiche Vorteile und Ermäßigungen in Zingst. Ihr erhaltet sie nach der Buchung automatisch per E-Mail.</p>
                            </PopoverContent>
                        </Popover>
                        {' '}senden wir euch im Anschluss automatisch per E-Mail zu. Weitere Infos auf{' '}
                        <a href="https://www.zingst.de/gaestekarte-vorteile-ermaessigungen" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-amber-700 hover:underline">
                            www.zingst.de
                        </a>
                    </p>
                </InfoCard>

                {!winter && (
                    <InfoCard
                        title="Winterinfo"
                        content="Im Winter (Oktober - April) bieten wir euch gesicherte, feste Stellplätze im vorderen Bereich auf Rasengittersteinen, da der hintere Bereich aufgrund möglicher Überschwemmungen geschlossen bleibt."
                        icon={<FaSnowflake className="h-full w-full" />}
                        delay={1.1}
                        inView={inView}
                    />
                )}
            </div>
        </div>
    );
}

interface InfoCardProps {
    title: string;
    content: string;
    children?: React.ReactNode;
    icon: React.ReactNode;
    delay?: number;
    inView?: boolean;
    winterNote?: string;
    highlight?: boolean;
}

function InfoCard({ title, content, children, icon, delay = 0, inView = true, winterNote, highlight }: InfoCardProps) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay }}
            className={`group relative ${highlight ? "md:col-span-2 lg:col-span-2" : ""}`}
        >
            {highlight && (
                <div className="absolute -top-3 left-6 z-20 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-900 shadow-lg">
                    Tipp
                </div>
            )}
            <div className={`relative h-full overflow-hidden rounded-3xl p-6 shadow-lg backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-xl ${highlight ? "border-2 border-amber-400 bg-gradient-to-br from-sky-100 to-amber-100/90 shadow-amber-500/30 hover:shadow-amber-500/40" : "bg-gradient-to-br from-white/90 to-amber-50/90 shadow-amber-900/10 hover:shadow-amber-900/20"}`}>
                {/* Background decoration */}
                <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-200/40 transition-all duration-500 group-hover:scale-150" />
                <div className="absolute -bottom-4 -left-4 h-16 w-16 rounded-full bg-amber-100/60 transition-all duration-700 group-hover:scale-125" />

                {/* Content */}
                <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-4 flex p-1 h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-amber-200 text-slate-700 shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                        {icon}
                    </div>

                    {/* Title */}
                    <h3 className="mb-2 font-special text-xl font-bold text-slate-800">
                        {title}
                    </h3>

                    {/* Content */}
                    {content && (
                        <p className="text-sm leading-relaxed text-slate-600">
                            {content}
                        </p>
                    )}

                    {/* Children (links, etc) */}
                    {children}

                    {/* Winter note */}
                    {winterNote && (
                        <div className="mt-4 flex items-center gap-2 rounded-full bg-slate-800/90 px-3 py-2 text-xs text-amber-400">
                            <FaCircleExclamation className="h-3 w-3 flex-shrink-0" />
                            <span>{winterNote}</span>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
